<script setup>
//#region Imports
import { ref, onBeforeMount, onUpdated, watch, reactive, getCurrentInstance, computed, nextTick } from 'vue';
import axios from 'axios';
import { inject } from 'vue';
import Pagination from "@/components/cPagination";
import PageDescription from "@/components/cPageDescription";
//#endregion

// Define emits
const emit = defineEmits(['close', 'save']);

// Define props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  tag_type_id: {
    type: Number,
    default: null
  },
  tag_grading_type_id: {
    type: Number,
    default: null
  },
  tag_classifier_type_id: {
    type: Number,
    default: null
  },
  existing_tags: {
    type: Array,
    default: () => []
  },
  hide_proficiency: {
    type: Boolean,
    default: false
  }
});

//#region Reactive
const loginState = inject('loginState');
const store = inject('store');

const addSkillData = reactive({
  selected: [],
  page:1,
  pageSize:20,
  filter_text: "",
  tagType: null,
  tagClassifierTypes: [],
  selectedTagClassifierTypes: [],
  tagGradingType: null
});

//#endregion
 
//#region Trash

//#endregion

//#region Computed

const addSkillFilteredList = computed(() => {
  if (!addSkillData.tagType) { return [] }
  let filtered = addSkillData.tagType?.values;
  if(addSkillData.tagClassifierTypes.length > 0){
    filtered = addSkillData.tagType?.values.filter(item => {
      return item.active && item.classifiers.some(classifier =>
        classifier.value && addSkillData.selectedTagClassifierTypes.includes(classifier.tag_classifier_type_value_id)
      );
    });
  }

  if(addSkillData.filter_text){
    filtered = filtered.filter(x => x.value.toLowerCase().includes(addSkillData.filter_text.toLowerCase()))
  }
  return filtered;
});

const addSkillFilteredListPage = computed(() => {
  const page = addSkillData.page;
  const pageSize = addSkillData.pageSize;
  return addSkillFilteredList.value.slice(pageSize * (page - 1), pageSize * page);
});

//#endregion

//#region Methods
function getInstance() {
  addSkillData.tagType = store.state.hierarchies.tagTypes.find(x => x.tag_type_id === props.tag_type_id);

  if(addSkillData.tagType && addSkillData.tagType.values) {
    addSkillData.tagType.values.forEach(item => {
      item.tag_rating_id = null;
      item.exists = props.existing_tags.some(existingTag => existingTag.tag_value_id === item.tag_value_id);
    });
  }
  
  if(props.tag_grading_type_id){
    addSkillData.tagGradingType = store.state.hierarchies.tagGradingTypes.find(x => x.tag_type_id === props.tag_grading_type_id)
  }

  const seen = new Set();
  const uniqueClassifiers = [];
  if(addSkillData.tagType.values){
    addSkillData.tagType.values.forEach(item => {
      item.classifiers.forEach(classifier => {
        if (classifier.value) {
          const key = `${classifier.tag_classifier_type_id}-${classifier.value}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueClassifiers.push(classifier);
          }
        }
      });
    });
  }

  addSkillData.tagClassifierTypes = uniqueClassifiers;
  addSkillData.selectedTagClassifierTypes = uniqueClassifiers.map(x => x.tag_classifier_type_value_id);
}

function getSkillTypeFilterText(selected, item, index) {
  if (item && index > 0) return "";
  let included = addSkillData.tagClassifierTypes.filter((s) =>
    selected.some((si) => s.tag_classifier_type_value_id === si)
  );
  if (included.length === addSkillData.tagClassifierTypes.length)
    return included.length > 0 ? "All" : "None";

  if (included.length > 2) return `${included.length} items`;

  return included.reduce((prev, curr) => {
    return prev + (prev ? ", " : "") + curr.value;
  }, "");
}

function initializeComponent() {
  getInstance();
}

function resetComponent() {
  addSkillData.selected = [];
  addSkillData.filter_text = "";
  addSkillData.page = 1;
  addSkillData.filter_text= "";
  addSkillData.tagType= null;
  addSkillData.tagClassifierTypes= [];
  addSkillData.selectedTagClassifierTypes= [];
  addSkillData.tagGradingType = null;
}

function handleClose() {
  resetComponent();
  emit('close', { destroy: true });
}

function handleSave() {
  emit('save', addSkillData.selected);
  resetComponent();
}

defineExpose({
  initializeComponent
});

watch(() => props.tag_type_id, (newVal) => {
  if (newVal) {
    initializeComponent();
  }
}, { immediate: true });

watch(() => props.show, (newVal) => {
  if (newVal === true) {
    initializeComponent();
  }
});

//#endregion

//#region Component
onUpdated(() => {
})

onBeforeMount(() => {
  getInstance();
})
//#endregion
</script>

<template>
  <v-card flat class="ma-0 pa-0">
    <v-card-text style="padding-top: 10px; overflow: hidden; padding-left:0px">
      <v-row dense>
        <v-col cols="3" v-if="addSkillData.tagClassifierTypes.length > 0">
          <v-select :items="addSkillData.tagClassifierTypes" v-model="addSkillData.selectedTagClassifierTypes" label="Skill Type" multiple dense outlined hide-details style="z-index: 10" item-text="value" item-value="tag_classifier_type_value_id"
            v-testid="'skill_add_selectedTagClassifierTypes'">
            <template v-slot:selection="{ item, index }">
              {{ getSkillTypeFilterText(addSkillData.selectedTagClassifierTypes, item, index) }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="6">
          <v-text-field clearable v-model="addSkillData.filter_text" placeholder="Search..." hide-details outlined dense
            v-testid="'skill_add_filter_text'"></v-text-field>
        </v-col>
      </v-row>      
      <v-row dense style="border: 1px solid #ccc; border-radius: 8px; max-height: calc(100vh - 350px); overflow: auto;">
        <v-col cols="6" v-for="(item,idx) in addSkillFilteredListPage" :key="`col_${item.tag_value_id}_${item.type_id}`">
          <div style="display: flex;align-content: center;align-items: center; border-radius: 8px; padding-left: 8px; height: 46px; border:1px solid #ccc;" :style="{'background-color': item.color}">
            <div style="width:30px; margin-bottom:6px">
              <v-checkbox
                v-if="!item.exists"
                v-model="addSkillData.selected"
                :label="item.name"
                :value="item"
                v-testid="'skill_add_selected_' + idx"
              ></v-checkbox>
              <v-checkbox
                v-else
                :label="item.name"
                disabled
                :input-value="true"
                v-testid="'skill_add_selected_' + idx"
              ></v-checkbox>
            </div>
            <div style="width:30px">
              <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="#aaa"
                >
                  mdi-information
                </v-icon>
              </template>
              <span>{{item.description}}</span>
            </v-tooltip>
            </div>                
            <div>{{ item.value }}</div>
            <div v-if="item.classifiers.filter(x => x.value).length">
              <v-chip v-for="(c,cIdx) in item.classifiers.filter(x => x.value)" :key="`${idx}_${cIdx}`"
                class="ma-2 pl-3 chipFont" small label
              >
                {{c.value}}
              </v-chip>
            </div>
            <div v-if="addSkillData.selected.includes(item) && !hide_proficiency" 
                style="max-height: 46px; overflow: hidden; margin-left: auto; padding-right: 10px;" class="select-container">
              <v-select hide-details dense v-model="item.tag_rating_id" :value="item.tag_rating_id" 
                style="width:130px; margin: 0; height: 32px;" 
                class="compact-select"
                :items="addSkillData.tagType.ratings" 
                item-value="tag_type_rating_id" 
                item-text="name"
                v-testid="'skill_add_tag_rating_id_' + idx">
                <template v-slot:selection="{ item }">
                  <span
                    style="width:100px; text-align: center; font-weight: 600; font-family: DM Sans !important;  font-size: 12px;"
                  >
                  {{ item.name  }}
                  </span>
                </template>    
                <template v-slot:label>
                  <span
                    style="width:100px; text-align: center; font-weight: 600; font-family: DM Sans !important;  font-size: 12px;"
                  >
                  {{ item.tag_rating_id ? '' : 'Set proficiency' }} 
                  </span>
                </template>                                                                        
              </v-select>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="d-flex justify-space-between px-6 pb-4">
      <PageDescription
        :totalItems="addSkillFilteredList.length"
        :pageSize="addSkillData.pageSize"
        :currentPage="addSkillData.page"
      />
      <Pagination
        v-if="addSkillFilteredList.length !== 0"
        :totalItems="addSkillData.tagType.values.length"
        :pageSize="addSkillData.pageSize"
        :currentPage="addSkillData.page"
        @pageNavigation="addSkillData.page = $event"
      />      
      <div>
        <v-btn class="ml-2" @click="handleClose">Cancel</v-btn>
        <v-btn class="ml-2" color="primary" @click="handleSave">Add Skills {{ addSkillData.selected.length > 0 ? `(${addSkillData.selected.length})` : '' }}</v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<style lang="scss" scoped>
.select-container {
  display: flex;
  align-items: center;
}

.compact-select ::v-deep .v-input__control {
  min-height: 32px !important;
  height: 32px !important;
}

.compact-select ::v-deep input {
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  position: absolute !important;
}

.compact-select ::v-deep .v-select__selection {
  margin-top: 4px !important;
  margin-bottom: 4px !important;
  max-height: 24px !important;
  overflow: hidden;
}

.compact-select ::v-deep .v-select__selections {
  padding: 0 4px !important;
  max-height: 15px;
}

.compact-select ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}

.compact-select ::v-deep .v-text-field__slot {
  height: 32px !important;
}
</style>